import jwtdecode from 'jwt-decode';
import axios from 'axios';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Intercepteur pour les requêtes sortantes
axios.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour les réponses
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Vérifier si le token existe encore
        const token = window.localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No token available');
        }

        // Vérifier si le token est expiré
        const { exp: expirationToken } = jwtdecode(token);
        if (expirationToken * 1000 <= new Date().getTime()) {
          throw new Error('Token expired');
        }

        // Le token semble valide, réessayer la requête
        processQueue(null, token);
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return axios(originalRequest);

      } catch (tokenError) {
        processQueue(tokenError, null);
        logout();
        
        // Éviter la redirection multiple
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function authenticate(credentials) {
  const ApiUrl = process.env.REACT_APP_PUBLIC_API + '/login';
  
  return axios({
    method: 'post',
    url: ApiUrl,
    data: credentials,
    headers: {
      'Content-Type': 'application/json'
    },
    withCredentials: false
  })
    .then((response) => {
      const data = response.data;
      window.localStorage.setItem('authToken', data.token);
      window.localStorage.setItem('customerId', data.id);
      axios.defaults.headers['Authorization'] = 'Bearer ' + data.token;
      return data;
    })
    .catch((error) => {
      console.error('Erreur détaillée:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config
      });
      throw error;
    });
}

function logout() {
  window.localStorage.removeItem('authToken');
  window.localStorage.removeItem('customerId');
  delete axios.defaults.headers['Authorization'];
}

function setAxiosToken(token) {
  axios.defaults.headers['Authorization'] = 'Bearer ' + token;
}

function setup() {
  const token = window.localStorage.getItem('authToken');
  if (token) {
    try {
      const { exp: expirationToken } = jwtdecode(token);
      if (expirationToken * 1000 > new Date().getTime()) {
        setAxiosToken(token);
        return true;
      } else {

      }
    } catch (error) {
      console.error('Erreur lors du décodage du token:', error);
    }
  }
  logout();
  return false;
}

function isAuthenticated() {
  const token = window.localStorage.getItem('authToken');
  if (token) {
    try {
      const { exp: expirationToken } = jwtdecode(token);
      if (expirationToken * 1000 > new Date().getTime()) {
        setAxiosToken(token);
        return true;
      } else {

        logout();
      }
    } catch (error) {
      console.error('Erreur lors du décodage du token:', error);
      logout();
    }
  }
  return false;
}

function getCustomerId() {
  const customerId = window.localStorage.getItem('customerId');
  const token = window.localStorage.getItem('authToken');
  if (customerId && token && isAuthenticated()) {
    return customerId;
  }
  return null;
}

const AuthAPI = {
  authenticate,
  logout,
  setAxiosToken,
  setup,
  isAuthenticated,
  getCustomerId,
};

export default AuthAPI;
